import React, { useState, useCallback, useRef, useEffect, createRef } from 'react';
import styles from '@stylesheet';
import { View, Alert, FlatList, Pressable, ScrollView, Image, StyleSheet, Platform } from 'react-native';
import { Text, Title, Card } from '@constants/Themed';
import LottieView from '@utils/lottieAnimation';
import { useLottieAnim } from '@hooks/lottieHook';
import * as Animatable from 'react-native-animatable';
import { useDropzone } from 'react-dropzone';
import UploadIcon from '@assets/animations/LordIcons/49-upload-file-solid-edited-black.json';
import { useDispatch, useSelector } from 'react-redux';
import previewData from './components/previewData';
import { GoogleDrive, OneDrive } from '@assets/--native';
import { ADDED_DATA_TO_STORE, CHANGE_DATA, CHANGE_DATA_TAGS, HIDE_ALL_MODALS, HIDE_LOADING_MODAL, REMOVE_DEFAULT_DATA_MARKER, SET_UPLOADER_STAGE, SHOW_AUTH_MODAL, SHOW_LOADING_MODAL } from '@reduxLocal/actions/types';
import { DefaultRootState } from '@reduxLocal/persistState';
import apis, { getGDriveFolderContent, getGDriveItem, uploadData } from '@api/api';
import { FontAwesome, MaterialCommunityIcons } from '@expo/vector-icons';
import { TextInput } from 'react-native-gesture-handler';
import { useTranslation } from 'react-i18next';
import DefaultScreen from '@components/Global/DefaultScreenView';
import GDriveModal from './components/GDriveModal';
import GlobalTutorial from '@components/TutorialComponent/GlobalTutorial';
import AnimatedLottieView from '@utils/lottieAnimation';
import FlipCard from 'react-native-flip-card';
import { AntDesign } from '@expo/vector-icons';
import SchemaPreviewer from './components/SchemaPreviewer';
import Papa from 'papaparse';
import parseByDelimiter from '@utils/parseByDelimiter';
import useGlobalUser from '@hooks/getGlobalUser';
import { navigationRef } from '../../../rootNavigation';
import * as DocumentPicker from 'expo-document-picker';
import Button from '@components/UI/Button/Button';
import { Buffer } from 'buffer';
import { googleAuths } from '@reduxLocal/selectors/userSelectors';
import RowComponent from '@components/Global/RowComponent/RowComponent';
import RobotHelper from '@components/RobotHelper/RobotHelper';
import IndividualDataTable from '@navigation/screens/LowLevel/Components/DataTable';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import getId from '@api/getId';
import { H4, Paragraph, XStack, YStack } from 'tamagui';

const carouselData = [
  {
    title: 'Easily upload existing data',
    caption: 'Get a flying start by using the data you already have',
    image: <AnimatedLottieView source={require('@assets/animations/spreadsheet.json')} style={[styles.centerEverything, { width: '40%', top: -10 }]} loop autoPlay />
  },
  {
    title: 'Just upload your CSV or excel file...',
    caption: 'You can upload your data and transform it in any way you see fit to suit your purpose',
    image: < AnimatedLottieView source={require('@assets/animations/upload.json')} style={{ width: '70%' }} loop autoPlay />
  },
  {
    title: '..And your team has access 👍',
    caption: "Couldn't be easier, right?",
    image: < AnimatedLottieView source={require('@assets/animations/teamwork.json')} style={{ width: '70%' }} loop autoPlay />
  }
];

const papaparseOptions = {
  dynamicTyping: true,
  skipEmptyLines: true,
  transformHeader: (header: string) =>
    header
      .toLowerCase()
      .replace(/\W/g, '_'),
  delimiter: '', // Auto-detect delimiter
  quoteChar: '"',
  escapeChar: '"',
  preview: 0, // Parse all rows
  fastMode: false // Use worker for large files
}

// Helper function to detect CSV delimiter
const detectDelimiter = (csvText: string): string => {
  const delimiters = [',', ';', '\t', '|'];
  const firstLine = csvText.split('\n')[0];
  let maxCount = 0;
  let bestDelimiter = ',';

  delimiters.forEach(delimiter => {
    const count = (firstLine.match(new RegExp(`\\${delimiter}`, 'g')) || []).length;
    if (count > maxCount) {
      maxCount = count;
      bestDelimiter = delimiter;
    }
  });

  return bestDelimiter;
}

// Helper function to detect data type from sample values
const detectDataType = (values: any[]): string => {
  if (!values || values.length === 0) return 'string';

  const nonEmptyValues = values.filter(v => v !== null && v !== undefined && v !== '');
  if (nonEmptyValues.length === 0) return 'string';

  // Check if all values are numbers
  const numberCount = nonEmptyValues.filter(v => !isNaN(Number(v)) && !isNaN(parseFloat(v))).length;
  if (numberCount === nonEmptyValues.length) {
    // Check if they're integers or floats
    const integerCount = nonEmptyValues.filter(v => Number.isInteger(Number(v))).length;
    return integerCount === nonEmptyValues.length ? 'integer' : 'number';
  }

  // Check if values look like dates
  const dateCount = nonEmptyValues.filter(v => !isNaN(Date.parse(v))).length;
  if (dateCount > nonEmptyValues.length * 0.8) { // 80% threshold
    return 'date';
  }

  // Check if values are boolean-like
  const boolCount = nonEmptyValues.filter(v =>
    typeof v === 'boolean' ||
    ['true', 'false', 'yes', 'no', '1', '0'].includes(String(v).toLowerCase())
  ).length;
  if (boolCount === nonEmptyValues.length) {
    return 'boolean';
  }

  return 'string';
}

// Helper function to validate file size (in MB)
const validateFileSize = (fileSize: number, maxSizeMB: number = 50): boolean => {
  const fileSizeMB = fileSize / (1024 * 1024);
  return fileSizeMB <= maxSizeMB;
}

// Helper function to format file size
const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

const UploadScreen = ({ navigation, route }) => {

  const { t } = useTranslation();
  const animationRef = useLottieAnim();
  const googleAuth = googleAuths();
  const [uploadHovered, setUploadHovered] = useState(false);
  const [data, setData] = useState();
  const [uploadVisible, setUploadVisible] = useState({ title: '', visible: false });
  const dataLoaded = useSelector((state: DefaultRootState) => state.app.data.dataImporter.dataHeld)
  const dataTracker = useSelector((state: DefaultRootState) => state.uploadPreview.present)

  const [retrievedFileList, setRetrievedFileList] = useState();
  const [searchFiles, setSearchFiles] = useState('');
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedItem, setSelectedItem] = useState();
  const currentCompany = useSelector((state: DefaultRootState) => state.app.selectedProfile);
  const [flip, setFlip] = useState(false);
  const [tutorialComplete, setTutorialComplete] = useState(false);
  const userId = useGlobalUser();
  const [gDriveAccessToken, setGDriveAccessToken] = useState(googleAuth?.accessToken);
  const googleDriveAuthentication = googleAuths()?.scopes?.includes('https://www.googleapis.com/auth/drive');
  const dispatch = useDispatch();
  const [loadingAI, setLoadingAI] = useState(false);
  const [aiComments, setAiComments] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedSheet, setSelectedSheet] = useState('');
  const [availableSheets, setAvailableSheets] = useState<string[]>([]);
  const [showSheetSelector, setShowSheetSelector] = useState(false);
  const [customDelimiter, setCustomDelimiter] = useState('');
  const [showDataPreview, setShowDataPreview] = useState(false);
  const [previewData, setPreviewData] = useState<any[]>([]);
  const theme = themeSelector();
  const [wideView, setWideView] = useState(false);
  //Get params from the navigation safely
  const shouldFlip = route?.params?.flip;

  useEffect(() => {
    dispatch({ type: SET_UPLOADER_STAGE, payload: 0 });
    if (shouldFlip) {
      setFlip(true);
    }
    return () => {
      dispatch({ type: SET_UPLOADER_STAGE, payload: 0 })
    }
  }, [])

  const _handleTutorialComplete = () => {
    setTutorialComplete(true);
  }

  type schemaObject = {
    title: string,
    type: string
  }

  function removeFileExtension(filename) {
    // Use a regular expression to match and remove the file extension
    return filename.replace(/\.[^/.]+$/, '');
  }


  const pickDocument = async () => {
    dispatch({ type: SHOW_LOADING_MODAL, data: { content: 'Selecting file...' } })
    setUploadProgress(0);

    try {
      const result = await DocumentPicker.getDocumentAsync({
        type: [
          'application/vnd.ms-excel',
          'text/csv',
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        ],
        copyToCacheDirectory: true
      }).catch((err) => {
        dispatch({ type: SHOW_AUTH_MODAL, data: { content: 'No file selected', type: 'error' } });
        return null;
      })

      if (!result || result.canceled) {
        dispatch({ type: HIDE_LOADING_MODAL });
        return;
      } else {
        const file = result.assets[0];

        // Validate file size (50MB limit)
        if (file.size && !validateFileSize(file.size, 50)) {
          dispatch({
            type: SHOW_AUTH_MODAL,
            data: {
              content: `File too large: ${formatFileSize(file.size)}. Maximum allowed size is 50MB.`,
              type: 'error'
            }
          });
          dispatch({ type: HIDE_LOADING_MODAL });
          return;
        }

        dispatch({ type: SHOW_LOADING_MODAL, data: { content: `Processing ${file.name}...` } });
        setUploadProgress(25);

        let csvText = '';

        if (
          file.name.endsWith('.csv') ||
          file.mimeType === 'text/csv'
        ) {
          // CSV: decode base64 to text
          setUploadProgress(50);
          const base64Content = file.uri.split(',')[1];
          if (!base64Content) {
            throw new Error('Invalid base64 data for CSV file');
          }
          csvText = Buffer.from(base64Content, 'base64').toString('utf-8');
          setUploadProgress(75);
        } else if (
          file.name.endsWith('.xls') ||
          file.name.endsWith('.xlsx') ||
          file.mimeType === 'application/vnd.ms-excel' ||
          file.mimeType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        ) {
          // Excel: use xlsx to parse and convert to CSV
          try {
            setUploadProgress(40);
            dispatch({ type: SHOW_LOADING_MODAL, data: { content: 'Reading Excel file...' } });

            // Dynamically import xlsx to avoid issues in environments where it's not available
            const XLSX = await import('xlsx');
            let fileData;

            if (file.uri.startsWith('file://')) {
              // If running in Expo Go or similar, fetch the file as blob/arraybuffer
              const response = await fetch(file.uri);
              if (!response.ok) {
                throw new Error(`Failed to fetch file: ${response.statusText}`);
              }
              const arrayBuffer = await response.arrayBuffer();
              fileData = new Uint8Array(arrayBuffer);
            } else if (file.uri.includes('base64,')) {
              // If base64 data URI
              const base64Content = file.uri.split(',')[1];
              if (!base64Content) {
                throw new Error('Invalid base64 data');
              }
              fileData = Buffer.from(base64Content, 'base64');
            } else {
              throw new Error('Unsupported file URI format');
            }

            setUploadProgress(60);
            dispatch({ type: SHOW_LOADING_MODAL, data: { content: 'Parsing Excel structure...' } });

            // Read the workbook with error handling
            const workbook = XLSX.read(fileData, {
              type: 'array',
              cellDates: true,
              cellNF: false,
              cellText: false,
              raw: false,
              // Additional options for better parsing
              codepage: 1252, // Handle different encodings
              dense: false, // Use sparse format for better memory usage
              sheetStubs: false // Don't include empty cells
            });

            if (!workbook.SheetNames || workbook.SheetNames.length === 0) {
              throw new Error('No sheets found in the Excel file');
            }

            // Multi-sheet support: if more than one sheet, let user choose
            if (workbook.SheetNames.length > 1) {
              setAvailableSheets(workbook.SheetNames);
              setShowSheetSelector(true);
              dispatch({ type: HIDE_LOADING_MODAL });

              // Store workbook for later use
              (window as any).tempWorkbook = workbook;
              return;
            }

            // Use the first sheet by default
            const firstSheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[firstSheetName];

            if (!worksheet) {
              throw new Error(`Sheet "${firstSheetName}" not found`);
            }

            // Get the range of the worksheet to check for data
            const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1:A1');
            if (range.e.r < 1) { // Less than 2 rows (header + data)
              throw new Error('The Excel sheet must contain at least a header row and one data row');
            }

            setUploadProgress(80);
            dispatch({ type: SHOW_LOADING_MODAL, data: { content: 'Converting to CSV format...' } });

            // Convert to CSV with proper handling of empty cells
            csvText = XLSX.utils.sheet_to_csv(worksheet, {
              forceQuotes: false,
              blankrows: false,
              strip: true,
              skipHidden: true, // Skip hidden rows/columns
              dateNF: 'yyyy-mm-dd' // Standardize date format
            });

            if (!csvText || csvText.trim() === '') {
              throw new Error('The Excel file appears to be empty or has no readable data');
            }
          } catch (xlsxError) {
            console.error('Excel parsing error:', xlsxError);
            dispatch({
              type: SHOW_AUTH_MODAL,
              data: {
                content: `Error parsing Excel file: ${xlsxError.message}`,
                type: 'error'
              }
            });
            dispatch({ type: HIDE_LOADING_MODAL });
            return;
          }
        } else {
          dispatch({ type: SHOW_AUTH_MODAL, data: { content: 'Unsupported file type', type: 'error' } });
          dispatch({ type: HIDE_LOADING_MODAL });
          return;
        }

        setUploadProgress(90);

        // Use the new processCSVData function
        await processCSVData(csvText, file.name);
      }
    } catch (error) {
      console.error('Error picking document:', error);
      dispatch({ type: HIDE_LOADING_MODAL });
      setUploadProgress(0);
    } finally {
      // Ensure modal is always dismissed, even if there are errors
      setTimeout(() => {
        dispatch({ type: HIDE_LOADING_MODAL });
        setUploadProgress(0);
      }, 100);
    }
  };

  // Function to handle sheet selection for multi-sheet Excel files
  const handleSheetSelection = async (sheetName: string) => {
    try {
      dispatch({ type: SHOW_LOADING_MODAL, data: { content: `Processing sheet: ${sheetName}...` } });
      setUploadProgress(70);

      const XLSX = await import('xlsx');
      const workbook = (window as any).tempWorkbook;
      if (!workbook) {
        throw new Error('Workbook data not found');
      }

      const worksheet = workbook.Sheets[sheetName];
      if (!worksheet) {
        throw new Error(`Sheet "${sheetName}" not found`);
      }

      // Get the range of the worksheet to check for data
      const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1:A1');
      if (range.e.r < 1) {
        throw new Error('The selected sheet must contain at least a header row and one data row');
      }

      setUploadProgress(85);

      // Convert to CSV
      const csvText = XLSX.utils.sheet_to_csv(worksheet, {
        forceQuotes: false,
        blankrows: false,
        strip: true,
        skipHidden: true,
        dateNF: 'yyyy-mm-dd'
      });

      if (!csvText || csvText.trim() === '') {
        throw new Error('The selected sheet appears to be empty or has no readable data');
      }

      setShowSheetSelector(false);
      setUploadProgress(90);

      // Continue with normal processing
      await processCSVData(csvText, `${sheetName}_data`);

      // Clean up
      delete (window as any).tempWorkbook;

    } catch (error) {
      console.error('Error processing selected sheet:', error);
      dispatch({
        type: SHOW_AUTH_MODAL,
        data: {
          content: `Error processing sheet: ${error.message}`,
          type: 'error'
        }
      });
      dispatch({ type: HIDE_LOADING_MODAL });
      setUploadProgress(0);
    } finally {
      // Ensure modal is always dismissed and sheet selector is closed
      setTimeout(() => {
        dispatch({ type: HIDE_LOADING_MODAL });
        setShowSheetSelector(false);
        setUploadProgress(0);
        delete (window as any).tempWorkbook;
      }, 100);
    }
  };

  // Function to process CSV data (extracted for reuse)
  const processCSVData = async (csvText: string, fileName: string) => {
    try {
      // Validate CSV content before parsing
      if (!csvText || csvText.trim() === '') {
        throw new Error("The file appears to be empty or contains no readable data.");
      }

      // Check if the content has at least one line that looks like headers
      const lines = csvText.trim().split('\n');
      if (lines.length < 2) {
        throw new Error("The file must contain at least a header row and one data row.");
      }

      // Remove first line if it includes Table or Tables
      const regex = /Table/gi;
      const firstLine = lines[0];
      let processedCsvText = csvText;
      if (regex.test(firstLine)) {
        processedCsvText = lines.slice(1).join('\n');
        if (lines.length < 3) {
          throw new Error("After removing table headers, the file must contain at least a header row and one data row.");
        }
      }

      setUploadProgress(95);
      dispatch({ type: SET_UPLOADER_STAGE, payload: 1 });

      await _handleGDriveItemSelect(processedCsvText, fileName).catch(err => {
        dispatch({ type: SHOW_AUTH_MODAL, data: { content: 'Error parsing file', type: 'error' } });
        throw err; // Re-throw to be caught by outer try-catch
      });

      setUploadProgress(100);
    } catch (error) {
      console.error('Error processing CSV data:', error);
      dispatch({ type: HIDE_LOADING_MODAL });
      dispatch({ type: SHOW_AUTH_MODAL, data: { content: error.message || 'Error processing file', type: 'error' } });
      setUploadProgress(0);
      throw error;
    }
  };

  const _handleGDriveItemSelect = (data, fileInfo) => {
    return new Promise(async (resolve, reject) => {
      try {
        let fields: Array<schemaObject> = [];

        // Detect delimiter for better parsing
        const delimiter = detectDelimiter(data);
        const parseOptions = { ...papaparseOptions, delimiter };

        const csvParse = Papa.parse(data, parseOptions);

        // Check for parsing errors
        if (csvParse.errors && csvParse.errors.length > 0) {
          console.warn('CSV parsing warnings:', csvParse.errors);
          // Only throw error for critical parsing issues
          const criticalErrors = csvParse.errors.filter(error => error.type === 'Delimiter' || error.type === 'Quotes');
          if (criticalErrors.length > 0) {
            throw new Error(`CSV parsing error: ${criticalErrors[0].message}`);
          }
        }


        function removeTrailingItem(string) {
          if (!string) return
          if (
            string.charAt(string.length - 1) === ',' ||
            string.charAt(string.length - 1) === ';' ||
            string.charAt(string.length - 1) === '_'
          ) {
            return string.slice(0, -1);
          }
          return string;
        }

        if (csvParse.data && Array.isArray(csvParse.data) && csvParse.data.length > 0) {
          const headerRow = csvParse.data[0];
          if (Array.isArray(headerRow) && headerRow.length > 0) {
            headerRow.forEach((item: any, index: number) => {
              const cleanedItem = removeTrailingItem(item);
              if (cleanedItem && cleanedItem.trim() !== '') {
                // Sample values from the column for type detection
                const columnValues = csvParse.data.slice(1, Math.min(11, csvParse.data.length)) // Sample first 10 rows
                  .map((row: any) => Array.isArray(row) ? row[index] : null)
                  .filter(val => val !== null && val !== undefined);

                const detectedType = detectDataType(columnValues);
                fields.push({ title: cleanedItem, type: detectedType });
              }
            });
          } else {
            throw new Error("Could not detect headers in your file, please ensure that your file is a CSV or Excel file and that it has headers.");
          }
        } else {
          throw new Error("Could not detect headers in your file, please ensure that your file is a CSV or Excel file and that it has headers.");
        }
        const dataLength = csvParse.data.length;
        const storedData = Papa.parse(data, { header: false });
        // Remove first item in storedData
        storedData.data.shift();

  

        dispatch({
          type: CHANGE_DATA,
          payload: {
            dataLength,
            schemaContent: fields,
            storedFile: storedData.data,
            isUpload: true,
            schemaTitle: parseByDelimiter(removeFileExtension(fileInfo)),
            dataTags: [],
            description: ''
          }
        });

        setAiComments('Looking for tags that match your data...');
        await fetch(`${process.env.EXPO_PUBLIC_AI_API}/generate-tags`, {
          method: "POST",
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ data: fields })
        })
          .then(res => res.json())
          .then(res => {
            setAiComments(`I found and applied the following tags: \n ${res}`);
            dispatch({ type: CHANGE_DATA_TAGS, payload: JSON.parse(res) });
            resolve(res);
          }).catch(err => {
            console.error("Error getting AI")
            setAiComments('Sorry, an error occurred while parsing the data');
          })
          .finally(() => {
            dispatch({ type: HIDE_LOADING_MODAL });
            setFlip(true);
          });
      } catch (error) {
        console.error('Error parsing data:', error);
        dispatch({ type: HIDE_ALL_MODALS });
        reject(error);
      } finally {
        // Ensure modal is always dismissed, even if there are errors
        setTimeout(() => {
          dispatch({ type: HIDE_LOADING_MODAL });
          setUploadProgress(0);
        }, 100);
      }
    });
  }


  const UploadOptionCard = ({ children, label, onPress, icon, title, description, ...props }: {
    children?: React.ReactNode;
    label?: string;
    onPress?: () => void | Promise<void>;
    icon?: React.ReactNode;
    title?: string;
    description?: string;
    [key: string]: any;
  }) => {
    return (
      <Card
        elevate
        flex={1}
        minHeight={120}
        alignItems="stretch"
        justifyContent="center"
        padding={0}
        onPress={onPress}
        hoverStyle={{ scale: 1.02, backgroundColor: '$backgroundHover' }}
        pressStyle={{ scale: 0.98 }}
        animation="bouncy"
        backgroundColor={theme ? "white" :"#1e293b"}
        borderRadius={12}
        overflow="hidden"
        {...props}
      >
        <XStack flexDirection={Platform.select({ web: 'row' })} flex={1} flexWrap='wrap' alignItems="stretch" height="100%">
          {/* Image/Icon Section */}
          <View
            style={{
              width: 170,
              minWidth: 100,
              flex: 1,
              backgroundColor: theme ? '#ececec': '#1e293b',
              justifyContent: 'center',
              alignItems: 'center',
              alignSelf: 'stretch',
              display: 'flex'
            }}
          >
            {icon || children}
          </View>

          {/* Content Section */}
          <YStack
            flex={3}
            padding={16}
            minWidth={100}
            justifyContent="center"
            alignItems="flex-start"
            gap={12}
          >
            <H4
              style={{
                fontSize: 18,
                fontWeight: '600',
                color: '#2563eb',
                marginBottom: 6
              }}
            >
              {title}
            </H4>
            <Paragraph
              style={{
                fontSize: 14,
                color: '#6b7280',
                lineHeight: 20,
                marginBottom: 16
              }}
            >
              {description}
            </Paragraph>
            <XStack
              alignItems="center"
              gap={8}
            >
              <Text style={{ color: '#2563eb', fontSize: 14, fontWeight: '500' }}>
                Let's go
              </Text>
              <AntDesign name="arrow-right" size={16} color="#2563eb" />
            </XStack>
          </YStack>
        </XStack>
      </Card>
    );
  };

  const MyDropZone = () => {
    return (
      <View style={{ margin: 5, flex: 1, borderRadius: 10, padding: 20, borderColor: '#ffffff66', gap: 20 }}>
        {/* Ensure size is the same as pressable above */}
        <UploadOptionCard
          label="Upload CSV/Excel File from your device"
          title="Upload from device"
          description="Upload your CSV or Excel file directly from your device for quick data processing and analysis."
          icon={
            <AnimatedLottieView
              style={{ height: 80, width: 80 }}
              source={UploadIcon}
              loop
              autoPlay
            />
          }
          onPress={pickDocument}
        />
        <UploadOptionCard
          label="Import supported files directly from an attached Google Drive"
          title="Upload from Google Drive"
          description="Import supported files directly from an attached Google Drive account with seamless integration."
          icon={
            <View style={{ justifyContent: 'center', alignItems: 'center', height: 80, width: 80 }}>
              <GoogleDrive height={80} width={80} />
            </View>
          }
          onPress={async () => {
            if (googleDriveAuthentication) {
              // ... your Google Drive logic
            } else {
              setModalVisible(true);
            }
          }}
        />

        <UploadOptionCard
          label="Import supported files directly from an attached OneDrive"
          title="Upload from OneDrive"
          description="Import supported files directly from an attached OneDrive account (coming soon)."
          icon={
            <View style={{ justifyContent: 'center', alignItems: 'center', height: 80, width: 80 }}>
              <OneDrive height={80} width={80} />
            </View>
          }
          onPress={() => {
            alert('OneDrive support coming soon');
          }}
        />
      </View>
    )
  }

  // Sheet Selector Modal Component
  const SheetSelectorModal = () => {
    if (!showSheetSelector) return null;

    return (
      <View style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000
      }}>
        <Card style={{
          backgroundColor: 'white',
          padding: 30,
          borderRadius: 15,
          maxWidth: 400,
          width: '90%',
          maxHeight: 500
        }}>
          <H4 style={{ textAlign: 'center', marginBottom: 20, color: '#2563eb' }}>
            Select Sheet to Import
          </H4>
          <Paragraph style={{ textAlign: 'center', marginBottom: 25, color: '#6b7280' }}>
            This Excel file contains multiple sheets. Please select which sheet you'd like to import:
          </Paragraph>

          <ScrollView style={{ maxHeight: 250 }}>
            {availableSheets.map((sheetName, index) => (
              <Pressable
                key={index}
                onPress={() => handleSheetSelection(sheetName)}
                style={{
                  padding: 15,
                  borderRadius: 8,
                  backgroundColor: '#f8fafc',
                  marginBottom: 10,
                  borderColor: '#e2e8f0'
                }}
              >
                <Text style={{ fontSize: 16, fontWeight: '500', color: '#1e293b' }}>
                  📊 {sheetName}
                </Text>
              </Pressable>
            ))}
          </ScrollView>

          <Pressable
            onPress={() => {
              setShowSheetSelector(false);
              setAvailableSheets([]);
              delete (window as any).tempWorkbook;
              dispatch({ type: HIDE_LOADING_MODAL });
            }}
            style={{
              marginTop: 20,
              padding: 12,
              borderRadius: 8,
              backgroundColor: '#ef4444',
              alignItems: 'center'
            }}
          >
            <Text style={{ color: 'white', fontWeight: '600' }}>Cancel</Text>
          </Pressable>
        </Card>
      </View>
    );
  };

  // Progress Indicator Component
  const ProgressIndicator = () => {
    if (uploadProgress === 0 || uploadProgress === 100) return null;

    return (
      <View style={{
        position: 'absolute',
        top: 20,
        left: 20,
        right: 20,
        backgroundColor: 'white',
        padding: 15,
        borderRadius: 10,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.25,
        shadowRadius: 3.84,
        elevation: 5,
        zIndex: 999
      }}>
        <Text style={{ fontSize: 14, fontWeight: '600', marginBottom: 8, color: '#374151' }}>
          Processing File... {uploadProgress}%
        </Text>
        <View style={{
          height: 6,
          backgroundColor: '#e5e7eb',
          borderRadius: 3,
          overflow: 'hidden'
        }}>
          <View style={{
            height: '100%',
            width: `${uploadProgress}%`,
            backgroundColor: '#3b82f6',
            borderRadius: 3
          }} />
        </View>
      </View>
    );
  };

  // Data Preview Modal Component
  const DataPreviewModal = () => {
    if (!showDataPreview) return null;

    return (
      <View style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000
      }}>
        <Card style={{
          backgroundColor: 'white',
          padding: 20,
          borderRadius: 15,
          maxWidth: '90%',
          width: '90%',
          maxHeight: '80%'
        }}>
          <H4 style={{ textAlign: 'center', marginBottom: 15, color: '#2563eb' }}>
            Data Preview
          </H4>
          <Paragraph style={{ textAlign: 'center', marginBottom: 20, color: '#6b7280' }}>
            Preview of the first 5 rows from your file:
          </Paragraph>

          <ScrollView horizontal>
            <ScrollView style={{ maxHeight: 300 }}>
              {previewData.length > 0 && (
                <View style={{ borderColor: '#e2e8f0', borderRadius: 8 }}>
                  {previewData.map((row, rowIndex) => (
                    <View key={rowIndex} style={{
                      flexDirection: 'row',
                      backgroundColor: rowIndex === 0 ? '#f8fafc' : 'white',
                      borderBottomWidth: rowIndex === previewData.length - 1 ? 0 : 1,
                      borderBottomColor: '#e2e8f0'
                    }}>
                      {Array.isArray(row) && row.map((cell, cellIndex) => (
                        <View key={cellIndex} style={{
                          padding: 8,
                          minWidth: 100,
                          borderRightWidth: cellIndex === row.length - 1 ? 0 : 1,
                          borderRightColor: '#e2e8f0'
                        }}>
                          <Text style={{
                            fontSize: 12,
                            fontWeight: rowIndex === 0 ? '600' : '400',
                            color: rowIndex === 0 ? '#374151' : '#6b7280'
                          }}>
                            {String(cell || '')}
                          </Text>
                        </View>
                      ))}
                    </View>
                  ))}
                </View>
              )}
            </ScrollView>
          </ScrollView>

          <View style={{ flexDirection: 'row', marginTop: 20, gap: 10 }}>
            <Pressable
              onPress={() => setShowDataPreview(false)}
              style={{
                flex: 1,
                padding: 12,
                borderRadius: 8,
                backgroundColor: '#ef4444',
                alignItems: 'center'
              }}
            >
              <Text style={{ color: 'white', fontWeight: '600' }}>Cancel</Text>
            </Pressable>

            <Pressable
              onPress={() => {
                setShowDataPreview(false);
                // Continue with processing
                // This would be called after user confirms the preview
              }}
              style={{
                flex: 1,
                padding: 12,
                borderRadius: 8,
                backgroundColor: '#22c55e',
                alignItems: 'center'
              }}
            >
              <Text style={{ color: 'white', fontWeight: '600' }}>Continue</Text>
            </Pressable>
          </View>
        </Card>
      </View>
    );
  };

  const _renderRetrievedFiles = ({ item }) => {
    const Item = ({ item }) => {
      const [hoveredState, setHoveredState] = useState(false);
      return (
        <Pressable onPress={async () => {
          if (item.mimeType == 'application/vnd.google-apps.folder') {
            await getGDriveFolderContent(gDriveAccessToken, item.id)
              .then(res => {
                if (res.status != 200) {
                  dispatch({ type: SHOW_AUTH_MODAL, data: { content: 'An error occurred', details: res } })
                }
                setRetrievedFileList(res.data?.files)
              })
          } else {
            await getGDriveItem(gDriveAccessToken, item.id, item.mimeType)
              .then(async res => {
                await _handleGDriveItemSelect(res.data, item.name);
              })
          }
        }} onHoverIn={() => setHoveredState(true)} onHoverOut={() => setHoveredState(false)}>
          <View style={{ flex: 1, borderBottomWidth: 1, borderColor: '#ececec66', flexDirection: 'row', minHeight: 35, backgroundColor: hoveredState ? '#ececec' : undefined }}>
            {
              item.mimeType === 'application/vnd.google-apps.folder' ?
                <FontAwesome name={"folder"} style={[styles.menuItemIcon, { flex: .02 }]} size={uploadVisible.visible ? 25 : 0} color={'black'} />
                :
                <FontAwesome name={"file-o"} style={[styles.menuItemIcon, { flex: .02 }]} size={uploadVisible.visible ? 25 : 0} color={'black'} />
            }
            <Text adjustsFontSizeToFit >{item.name}</Text>
          </View>
        </Pressable>
      )
    }

    return (
      <Item item={item} />
    )
  }

  return (
    <DefaultScreen layoutCallback={(layout) => { setWideView(layout.width > 850) }} style={[styles.pageContainer, { alignSelf: 'stretch', maxHeight: '100%' }]} screenTitle={'Upload Data'} noMargin backButton scrollDisabled>
      <GDriveModal visible={modalVisible} setVisible={setModalVisible} />
      <ProgressIndicator />
      <SheetSelectorModal />
      <DataPreviewModal />
      <View style={[styles.contentContainer, { flex: 1, flexDirection: 'row', alignSelf: 'stretch', paddingHorizontal: 50, paddingRight: 90, paddingBottom: 50 }]}>
        <FlipCard
          style={{ flex: 1, alignSelf: 'stretch' }}
          flipHorizontal={true}
          flipVertical={false}
          flip={flip}
          clickable={false}
        >
          <View style={styles.face}>
            <View style={{ flexDirection: 'row', alignSelf: "stretch", flex: 5 }}>
              {
                !tutorialComplete && wideView &&
                <View style={[styles.centerEverything, { flex: 2, left: -20, top: -50, justifyContent: 'center' }]}>
                  <GlobalTutorial customWidth={450} customHeight={450} carouselData={carouselData} showArrows completeCallback={_handleTutorialComplete} />
                </View>
              }
              <Animatable.View transition={'flex'} style={{ flex: tutorialComplete ? 2.5 : 2 }}>
                <Animatable.View style={{ alignSelf: 'stretch', flex: dataLoaded ? 0.00001 : 3, opacity: dataLoaded ? 0 : 1, padding: 20 }} transition={'flex'}>
                  {
                    uploadVisible.title ?
                      <Animatable.View style={{ borderRadius: 20, opacity: uploadVisible.title ? 1 : 0, elevation: 1, shadowRadius: 10, shadowOpacity: .2, alignSelf: 'stretch', flex: uploadVisible.title ? 3 : 0.00001, backgroundColor: 'white' }} transition={['flex', 'opacity']} >
                        <View style={{ flex: 0.15, alignSelf: 'stretch', backgroundColor: '#ececec', borderTopEndRadius: 20, paddingHorizontal: 15, padding: 3, borderTopLeftRadius: 20 }}>
                          <View style={{ flex: 1, flexDirection: 'row', alignItems: 'center', alignSelf: 'stretch', justifyContent: 'center', gap: 15 }}>
                            <Image source={require('@assets/--native/Icons/Google_Drive_icon_(2020)')} style={{ width: 30, height: 30 }} />
                            <Text adjustsFontSizeToFit style={{ flex: .9, fontWeight: 'bold', verticalAlign: 'middle' }}>{uploadVisible.title}</Text>
                            <Pressable onPress={() => { setUploadVisible({ title: '', visible: false }) }} style={{ flex: .1, alignSelf: 'center' }}>
                              <FontAwesome name={"close"} style={[styles.menuItemIcon, { flex: 1, alignSelf: 'center' }]} size={uploadVisible.visible ? 20 : 0} color={'black'} />
                            </Pressable>
                          </View>
                        </View>
                        <View style={{ flex: .15, flexDirection: 'row', alignSelf: 'stretch', padding: 10, maxHeight: 50, gap: 50 }}>
                          <TextInput value={searchFiles} style={{ backgroundColor: 'white', flex: 0.8 }} onChangeText={(value) => setSearchFiles(value)} onBlur={() => { console.log('searched for a file') }} placeholder={'Search for files'} />
                          <Pressable onPress={() => { }} style={{ flex: 0.2 }}>
                            <MaterialCommunityIcons name={"folder-upload"} style={[styles.menuItemIcon, { flex: 1, alignSelf: 'center' }]} size={uploadVisible.visible ? 20 : 0} color={'black'} />
                          </Pressable>
                        </View>
                        <View style={{ flex: 1, overflow: 'hidden', borderRadius: 20, paddingHorizontal: 5 }}>
                          <FlatList
                            ListEmptyComponent={() => { return (<Text adjustsFontSizeToFit>No files found in folder!</Text>) }}
                            data={retrievedFileList?.sort((a, b) => {
                              if (a.mimeType === 'application/vnd.google-apps.folder') {
                                a.name.localeCompare(b)
                              }
                            })}
                            contentContainerStyle={{ flex: 1 }}
                            renderItem={(item) => _renderRetrievedFiles(item)}
                            style={{ padding: 20, paddingVertical: 20, borderRadius: 20, flex: 1, opacity: 1, maxHeight: 400 }} />
                        </View>
                      </Animatable.View>
                      :
                      <View style={{ flex: 1 }}>
                        <MyDropZone />
                      </View>

                  }
                </Animatable.View>
              </Animatable.View>
            </View>
          </View>
          <View style={[styles.back, { flex: 1, alignSelf: 'stretch' }]}>
            <View style={[styles.centerEverything, { flexDirection: 'row', flex: 1, alignSelf: 'stretch', justifyContent: "space-around" }]}>
              <View style={{ flex: 1, gap: "5%", left: -30, maxWidth: 60 }}>
                <Pressable style={[styles.LoginButton, { backgroundColor: 'rgb(90,192,234)', flex: 1, gap: 5, padding: 30 }]} onPress={() => { setSelectedItem(undefined); setFlip(false); dispatch({ type: SET_UPLOADER_STAGE, payload: 0 }) }}>
                  <AntDesign name="arrow-left" size={25} color="white" />
                  <Text adjustsFontSizeToFit style={{ color: 'white', fontWeight: 'bold', fontSize: 16 }}>Back</Text>
                </Pressable>
                <Pressable style={[styles.LoginButton, { backgroundColor: 'rgb(90,192,234)', flex: 1, gap: 5, padding: 30 }]} onPress={() => navigation.navigate('DataHandler')}>
                  <AntDesign name="edit" size={25} color="white" />
                  <Text adjustsFontSizeToFit style={{ color: 'white', fontWeight: 'bold', fontSize: 16 }}>Edit</Text>
                </Pressable>
                <Pressable style={[styles.LoginButton, { backgroundColor: 'rgb(90,192,234)', flex: 1, gap: 5, padding: 30 }]}
                  onPress={async () => {
                    if (!dataTracker.storedFile) {
                      alert('No data to save!')
                    } else if (dataTracker.storedFile.length > 0) {
                      await uploadData({ id: getId(), data: { dataTitle: dataTracker.dataTitle, dataTags: dataTracker.dataTags, dataContent: dataTracker.storedFile, dataSchema: dataTracker.dataHeld } }).then((res) => {
                        if (res.success) {
                          dispatch({ type: ADDED_DATA_TO_STORE, log: true })
                          navigationRef.navigate('DataStore');
                        }
                      })
                    } else {
                      await apis.saveData({ id: getId(), data: { dataTitle: dataTracker.dataTitle, dataTags: dataTracker.dataTags, dataContent: dataTracker.storedFile, dataSchema: dataTracker.dataHeld } }).then((res) => {
                        if (res.success) {
                          dispatch({ type: ADDED_DATA_TO_STORE, log: true })
                          navigationRef.navigate('DataStore');
                        }
                      })
                    }
                  }}
                >
                  <AntDesign name="save" size={25} color="white" />
                  <Text adjustsFontSizeToFit style={{ color: 'white', fontWeight: 'bold', fontSize: 16 }}>Save</Text>
                </Pressable>
              </View>
              <View style={{ flex: 7, flexDirection: 'column', alignContent: 'stretch', alignSelf: 'stretch', height: '100%', gap: 30 }}>
                <View style={{ flexDirection: "row", flex: 1, elevation: 1, shadowRadius: 5, shadowOpacity: .4, borderRadius: 20, padding: 20 }}>
                  <View style={{ flex: 1 }}>
                    <RowComponent style={{ flexDirection: "row" }} title='File Name' forceRow customComponent={<Text style={styles.centerEverything}>{dataTracker.dataTitle}</Text>} />
                    <RowComponent style={{ flexDirection: "row" }} title='No. of Docs' forceRow customComponent={<Text style={styles.centerEverything}>{dataTracker.dataLength}</Text>} />
                  </View>
                  <View style={{ flex: 1 }}>
                    <RobotHelper fontColor='black' loadingText='Loading comments, but you can continue...' isLoading={loadingAI} inverse value={aiComments} fontSize={16} />
                  </View>
                </View>
                <View style={{ flex: 8, flexDirection: "row", gap: 30 }}>
                  <ScrollView style={{ flex: 1, elevation: 1, minWidth: 400, shadowRadius: 5, shadowOpacity: .4, borderRadius: 20, padding: 20 }}>
                    <SchemaPreviewer interaction={false} />
                  </ScrollView>
                  <View style={{ flex: 2 }}>
                    <IndividualDataTable plain={!shouldFlip} clickFunction={(data) => console.log(data)} dataArray={dataTracker?.storedFile} dataSchema={dataTracker?.dataHeld} />
                  </View>
                </View>
              </View>
            </View>
          </View>
        </FlipCard>
      </View>
    </DefaultScreen >
  );
}

const uploadScreenStyles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#f7f7f7',
  },
  title: {
    fontSize: 20,
    fontWeight: 'bold',
    textAlign: 'center',
    marginBottom: 20,
  },
  dropzone: {
    flex: 1,
    borderWidth: 2,
    borderRadius: 20,
    borderColor: '#eeeeee',
    borderStyle: 'dashed',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#fafafa',
    padding: 20,
    margin: 20,
  },
  dropzoneText: {
    fontSize: 16,
    textAlign: 'center',
  },
  fileList: {
    flex: 1,
    margin: 20,
  },
  fileItem: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 10,
    borderBottomWidth: 1,
    borderBottomColor: '#eeeeee',
  },
  fileIcon: {
    marginRight: 10,
  },
  fileName: {
    flex: 1,
  },
  fileActions: {
    flexDirection: 'row',
  },
  fileAction: {
    marginLeft: 10,
  },
  fileActionText: {
    color: '#007aff',
  },
  preview: {
    flex: 1,
    margin: 20,
    padding: 20,
    borderRadius: 20,
    backgroundColor: '#ffffff',
  },
  previewText: {
    fontSize: 16,
  },
  previewData: {
    flex: 1,
    margin: 20,
    padding: 20,
    borderRadius: 20,
    backgroundColor: '#ffffff',
  },
  previewDataText: {
    fontSize: 16,
  },
  previewDataList: {
    flex: 1,
  },
  previewDataItem: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 10,
    borderBottomWidth: 1,
    borderBottomColor: '#eeeeee',
  },
  previewDataItemIcon: {
    marginRight: 10,
  },
  previewDataItemText: {
    flex: 1,
  },
  previewDataItemActions: {
    flexDirection: 'row',
  },
  previewDataItemAction: {
    marginLeft: 10,
  },
});

export default UploadScreen;